import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getTranslatedProductName, getTranslatedCategory } from '../../utils/translationHelpers';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Star,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  OptimizedFormContainer,
  OptimizedFormSection,
  OptimizedFormGrid,
  OptimizedProductGrid,
  OptimizedProductCard,
  OptimizedOrderSummary,
  OptimizedSubmitButton,
  SearchAndFilterContainer,
  SearchInput,
  FilterSelect,
  CollapsibleSection,
  CategoryBadge,
  SectionTitle,
  FieldContainer,
  QuantityControl,
  SuccessMessage,
  ErrorMessage,
  LoadingSpinner,
  GlobalOrderSummary
} from './shared/OptimizedFormComponents';
import {
  CustomerInformationSection,
  OrderInformationSection,
  SharedOrderFormData,
  getMockCustomerData
} from './shared/SharedFormSections';
import { useCustomerData } from './shared/CustomerDataService';
import { useGlobalOrderData, OrderItem } from '../../hooks/useGlobalOrderData';
import { FormStatusIndicator } from './shared/FormStatusIndicator';
import { PreviewOrderButton } from './shared/PreviewOrderButton';
import { BackToTopButton } from './shared/BackToTopButton';
import { ThemeColors, Spacing, Typography } from "../../styles/ThemeStyles";

interface OrderItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: 'organic' | 'heritage' | 'seedtape';
}

interface OrderFormData extends SharedOrderFormData {
  items: OrderItem[];
}

// Complete product data based on CSV analysis
const heritageProducts = [
  // $3.79 Organic
  { id: '1', name: 'Aunt Mollys Ground Cherry', code: '143631', price: 3.79, category: 'organic' as const },
  { id: '2', name: 'Bean Gold Rush', code: '143546', price: 3.79, category: 'organic' as const },
  { id: '3', name: 'Bean Golden Wax Bush', code: '143547', price: 3.79, category: 'organic' as const },
  { id: '4', name: 'Bean Kentucky Wonder (Pole) -H', code: '143548', price: 3.79, category: 'organic' as const },
  { id: '5', name: 'Bean Provider Bush', code: '143549', price: 3.79, category: 'organic' as const },
  { id: '6', name: 'Bean Tendergreen Improved - H', code: '143550', price: 3.79, category: 'organic' as const },
  { id: '7', name: 'Beet Cylindra', code: '143552', price: 3.79, category: 'organic' as const },
  { id: '8', name: 'Beet Detroit Dark Red - H', code: '143553', price: 3.79, category: 'organic' as const },
  { id: '9', name: 'Beet Early Wonder Tall Top - H', code: '143554', price: 3.79, category: 'organic' as const },
  { id: '10', name: 'Beet Green Top Bunching', code: '143555', price: 3.79, category: 'organic' as const },
  { id: '11', name: 'Broccoli Decicco', code: '143556', price: 3.79, category: 'organic' as const },
  { id: '12', name: 'Broccoli Green Sprouting - H', code: '143557', price: 3.79, category: 'organic' as const },
  { id: '13', name: 'Cabbage Early Golden Acre', code: '143558', price: 3.79, category: 'organic' as const },
  { id: '14', name: 'Cantaloupe Hearts of Gold - H', code: '143559', price: 3.79, category: 'organic' as const },
  { id: '15', name: 'Cantaloupe Sierra Gold', code: '143560', price: 3.79, category: 'organic' as const },
  { id: '16', name: 'Carrot Chantenay', code: '143561', price: 3.79, category: 'organic' as const },
  { id: '17', name: 'Carrot Imperator', code: '143562', price: 3.79, category: 'organic' as const },
  { id: '18', name: 'Carrot Scarlet Nantes - H', code: '143565', price: 3.79, category: 'organic' as const },
  { id: '19', name: 'Carrot Rainbow Mix', code: '143564', price: 3.79, category: 'organic' as const },
  { id: '20', name: 'Carrot Nantes Touchon - H', code: '143566', price: 3.79, category: 'organic' as const },
  { id: '21', name: 'Cauliflower Early Snowball', code: '143568', price: 3.79, category: 'organic' as const },
  { id: '22', name: 'Cucumber Marketmore', code: '143569', price: 3.79, category: 'organic' as const },
  { id: '23', name: 'Cucumber Muncher', code: '143570', price: 3.79, category: 'organic' as const },
  { id: '24', name: 'Cucumber National Pickling - H', code: '143571', price: 3.79, category: 'organic' as const },
  { id: '25', name: 'Cucumber Spacemaster', code: '143572', price: 3.79, category: 'organic' as const },
  { id: '26', name: 'Cucumber Straight Eight - H', code: '143573', price: 3.79, category: 'organic' as const },
  { id: '27', name: 'Cucumber Sumter', code: '143574', price: 3.79, category: 'organic' as const },
  { id: '28', name: 'Cucumber Tendergreen', code: '143575', price: 3.79, category: 'organic' as const },
  { id: '29', name: 'Herb Arugula - H', code: '143576', price: 3.79, category: 'organic' as const },
  { id: '30', name: 'Herb Basil Cinnamon', code: '143577', price: 3.79, category: 'organic' as const },
  { id: '31', name: 'Herb Basil Dark Opal', code: '143578', price: 3.79, category: 'organic' as const },
  { id: '32', name: 'Herb Basil Sweet - H', code: '143579', price: 3.79, category: 'organic' as const },
  { id: '33', name: 'Herb Chives - H', code: '143580', price: 3.79, category: 'organic' as const },
  { id: '34', name: 'Herb Cilantro - H', code: '143581', price: 3.79, category: 'organic' as const },
  { id: '35', name: 'Herb Dill - H', code: '143582', price: 3.79, category: 'organic' as const },
  { id: '36', name: 'Herb Garlic Chives - H', code: '143583', price: 3.79, category: 'organic' as const },
  { id: '37', name: 'Herb Oregano', code: '143584', price: 3.79, category: 'organic' as const },
  { id: '38', name: 'Herb Parsley Italian Dark Green', code: '143585', price: 3.79, category: 'organic' as const },
  { id: '39', name: 'Herb Parsley Moss Curled', code: '143586', price: 3.79, category: 'organic' as const },
  { id: '40', name: 'Herb Sage', code: '143587', price: 3.79, category: 'organic' as const },
  { id: '41', name: 'Herb Thyme - H', code: '143588', price: 3.79, category: 'organic' as const },
  { id: '42', name: 'Kale Siberian', code: '143589', price: 3.79, category: 'organic' as const },
  { id: '43', name: 'Kohi Rabi White Vienna', code: '143590', price: 3.79, category: 'organic' as const },
  { id: '44', name: 'Lettuce Bibb', code: '143592', price: 3.79, category: 'organic' as const },
  { id: '45', name: 'Lettuce Black Seeded Simpson', code: '143593', price: 3.79, category: 'organic' as const },
  { id: '46', name: 'Lettuce Buttercrunch - H', code: '143594', price: 3.79, category: 'organic' as const },
  { id: '47', name: 'Lettuce Iceberg', code: '143595', price: 3.79, category: 'organic' as const },
  { id: '48', name: 'Lettuce Red Sails', code: '143597', price: 3.79, category: 'organic' as const },
  { id: '49', name: 'Lettuce Romaine - H', code: '143598', price: 3.79, category: 'organic' as const },
  { id: '50', name: 'Lettuce Salad Bowl', code: '143599', price: 3.79, category: 'organic' as const },
  { id: '51', name: 'Mesclun Gourmet Greens Mixture', code: '143600', price: 3.79, category: 'organic' as const },
  { id: '52', name: 'Onion Evergreen Bunching', code: '143601', price: 3.79, category: 'organic' as const },
  { id: '53', name: 'Pea Green Arrow - H', code: '143603', price: 3.79, category: 'organic' as const },
  { id: '54', name: 'Pea Oregon Sugar Pod', code: '143604', price: 3.79, category: 'organic' as const },
  { id: '55', name: 'Pea Sugar Daddy', code: '143605', price: 3.79, category: 'organic' as const },
  { id: '56', name: 'Pea Tom Thumb - H', code: '143606', price: 3.79, category: 'organic' as const },
  { id: '57', name: 'Pepper California Wonder - H', code: '143607', price: 3.79, category: 'organic' as const },
  { id: '58', name: 'Pepper Golden California Wonder - H', code: '143608', price: 3.79, category: 'organic' as const },
  { id: '59', name: 'Pepper Long Red Cayenne Slim', code: '143609', price: 3.79, category: 'organic' as const },
  { id: '60', name: 'Pepper Rainbow Blend', code: '143610', price: 3.79, category: 'organic' as const },
  { id: '61', name: 'Pepper Sweet Banana', code: '143611', price: 3.79, category: 'organic' as const },
  { id: '62', name: 'Pumpkin Big Max', code: '143612', price: 3.79, category: 'organic' as const },
  { id: '63', name: 'Pumpkin Small Sugar', code: '143613', price: 3.79, category: 'organic' as const },
  { id: '64', name: 'Radish Cherry Belle - H', code: '143614', price: 3.79, category: 'organic' as const },
  { id: '65', name: 'Radish Easter Egg Blend', code: '143615', price: 3.79, category: 'organic' as const },
  { id: '66', name: 'Radish French Breakfast', code: '143616', price: 3.79, category: 'organic' as const },
  { id: '67', name: 'Radish Sparkler White Tip', code: '143617', price: 3.79, category: 'organic' as const },
  { id: '68', name: 'Spinach Bloomsdale Long Standing', code: '143618', price: 3.79, category: 'organic' as const },
  { id: '69', name: 'Spinach Viroflay', code: '143619', price: 3.79, category: 'organic' as const },
  { id: '70', name: 'Squash Buttercup', code: '143622', price: 3.79, category: 'organic' as const },
  { id: '71', name: 'Squash Early Bush Scallop', code: '143623', price: 3.79, category: 'organic' as const },
  { id: '72', name: 'Squash Early Prolific Straighneck - H', code: '143624', price: 3.79, category: 'organic' as const },
  { id: '73', name: 'Squash Table Queen', code: '143625', price: 3.79, category: 'organic' as const },
  { id: '74', name: 'Squash Vegetable Spaghetti - H', code: '143626', price: 3.79, category: 'organic' as const },
  { id: '75', name: 'Sunflower Giant', code: '143627', price: 3.79, category: 'organic' as const },
  { id: '76', name: 'Swiss Chard Fordhook - H', code: '143628', price: 3.79, category: 'organic' as const },
  { id: '77', name: 'Swiss Chard Rainbow Blend', code: '143629', price: 3.79, category: 'organic' as const },
  { id: '78', name: 'Swiss Chard Ruby Red', code: '143630', price: 3.79, category: 'organic' as const },
  { id: '79', name: 'Tomato Beefsteak', code: '143632', price: 3.79, category: 'organic' as const },
  { id: '80', name: 'Tomato Brandywine Red - H', code: '143633', price: 3.79, category: 'organic' as const },
  { id: '81', name: 'Tomato Delicious', code: '143634', price: 3.79, category: 'organic' as const },
  { id: '82', name: 'Tomato Rainbow Cherry', code: '143635', price: 3.79, category: 'organic' as const },
  { id: '83', name: 'Tomato Roma - H', code: '143636', price: 3.79, category: 'organic' as const },
  { id: '84', name: 'Tomato San Marzano', code: '143637', price: 3.79, category: 'organic' as const },
  { id: '85', name: 'Tomato Sweetie', code: '143638', price: 3.79, category: 'organic' as const },
  { id: '86', name: 'Tomato Yellow Pearshaped', code: '143639', price: 3.79, category: 'organic' as const },
  { id: '87', name: 'Turnip Purple Top', code: '143640', price: 3.79, category: 'organic' as const },
  { id: '88', name: 'Watermelon Sugar Baby', code: '143642', price: 3.79, category: 'organic' as const },
  { id: '89', name: 'Zucchini Black Beauty - H', code: '143643', price: 3.79, category: 'organic' as const },
  { id: '90', name: 'Zucchini Foodhook', code: '143644', price: 3.79, category: 'organic' as const },
  
  // $4.99 Seedtape
  { id: '91', name: 'Radish Cherry Belle ST - H', code: '139830', price: 4.99, category: 'seedtape' as const },
  { id: '92', name: 'Radish French Breakfast ST - H', code: '139833', price: 4.99, category: 'seedtape' as const },
  { id: '93', name: 'Beet Detroit Dark Red ST - H', code: '139825', price: 4.99, category: 'seedtape' as const },
  { id: '94', name: 'Carrot Red Cored Chantenay ST - H', code: '139834', price: 4.99, category: 'seedtape' as const },
  { id: '95', name: 'Carrot Scarlet Nantes ST - H', code: '139826', price: 4.99, category: 'seedtape' as const },
  { id: '96', name: 'Cucumber National Pickling ST - H', code: '139827', price: 4.99, category: 'seedtape' as const },
  { id: '97', name: 'Lettuce Cos or Romaine ST - H', code: '139832', price: 4.99, category: 'seedtape' as const },
  { id: '98', name: 'Lettuce Grand Rapids ST - H', code: '139828', price: 4.99, category: 'seedtape' as const },
  { id: '99', name: 'Onion White Lisbon Bunching ST', code: '139829', price: 4.99, category: 'seedtape' as const },
  
  // $5.99 Vegetable Heritage
  { id: '100', name: 'Bean Blue Lake Pole', code: '143656', price: 5.99, category: 'heritage' as const },
  { id: '101', name: 'Bean Gold Rush', code: '144019', price: 5.99, category: 'heritage' as const },
  { id: '102', name: 'Bean Improved Golden Wax Bush', code: '143663', price: 5.99, category: 'heritage' as const },
  { id: '103', name: 'Bean Stringless Green Pod Bush - H', code: '143677', price: 5.99, category: 'heritage' as const },
  { id: '104', name: 'Bean Tendergreen Improved Bush - H', code: '143679', price: 5.99, category: 'heritage' as const },
  { id: '105', name: 'Beet Cylindra - H', code: '143683', price: 5.99, category: 'heritage' as const },
  { id: '106', name: 'Beet Detroit Dark Red - H', code: '143685', price: 5.99, category: 'heritage' as const },
  { id: '107', name: 'Carrot Red Cored Danvers - H', code: '143703', price: 5.99, category: 'heritage' as const },
  { id: '108', name: 'Carrot Scarlet Nantes Coreless - H', code: '143705', price: 5.99, category: 'heritage' as const },
  { id: '109', name: 'Corn Early Sunglow', code: '143713', price: 5.99, category: 'heritage' as const },
  { id: '110', name: 'Corn Honey and CreamBicolour', code: '143716', price: 5.99, category: 'heritage' as const },
  { id: '111', name: 'Cucumber Improved Long Green', code: '143724', price: 5.99, category: 'heritage' as const },
  { id: '112', name: 'Cucumber National Pickling - H', code: '143727', price: 5.99, category: 'heritage' as const },
  { id: '113', name: 'Lettuce Grand Rapids - H', code: '143746', price: 5.99, category: 'heritage' as const },
  { id: '114', name: 'Pea Green Arrow - H', code: '143767', price: 5.99, category: 'heritage' as const },
  { id: '115', name: 'Pea Laxtons Progress - H', code: '143769', price: 5.99, category: 'heritage' as const },
  { id: '116', name: 'Pea Lincoln Homesteader - H', code: '143771', price: 5.99, category: 'heritage' as const },
  { id: '117', name: 'Pea Sugar Snap - H', code: '143776', price: 5.99, category: 'heritage' as const },
  { id: '118', name: 'Radish Cherry Belle', code: '143790', price: 5.99, category: 'heritage' as const },
  { id: '119', name: 'Spinach Bloomsdale', code: '143801', price: 5.99, category: 'heritage' as const },
  { id: '120', name: 'Swiss Chard Fordhook Giant - H', code: '143814', price: 5.99, category: 'heritage' as const },
  { id: '121', name: 'Carrot Red Cored Chantenay', code: '143646', price: 5.99, category: 'heritage' as const },
  { id: '122', name: 'Carrot Scarlet Nantes - H', code: '143647', price: 5.99, category: 'heritage' as const },
  { id: '123', name: 'Cucumber National Pickling - H', code: '143648', price: 5.99, category: 'heritage' as const },
  { id: '124', name: 'Mesclun Salad Mix', code: '143651', price: 5.99, category: 'heritage' as const },
  { id: '125', name: 'Spinach Long Standing Bloomsdale', code: '143652', price: 5.99, category: 'heritage' as const },
];

const FormValidation = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const ProductCategorySection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md};
  background: ${ThemeColors.primary.light};
  border-radius: ${Spacing.sm};
  
  .category-info {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    
    .category-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
    }
    
    .category-count {
      background: ${ThemeColors.primary.main};
      color: white;
      padding: ${Spacing.xs} ${Spacing.sm};
      border-radius: ${Spacing.sm};
      font-size: ${Typography.fontSize.sm};
      font-weight: ${Typography.fontWeight.semibold};
    }
  }
  
  .category-price {
    font-size: ${Typography.fontSize.xl};
    font-weight: ${Typography.fontWeight.bold};
    color: ${ThemeColors.primary.main};
  }
`;

export const OptimizedHeritageOrderForm: React.FC<{ storeId?: string; onShowPreview?: () => void }> = ({ storeId, onShowPreview }) => {
  const { t } = useTranslation();
  
  // Use global order data with cache functionality
  const { 
    orderData, 
    updateOrderData, 
    updateHeritageItems,
    submitOrder,
    isLoading: globalLoading,
    error: globalError,
    getFormStatus,
    autoSave,
    clearDraft
  } = useGlobalOrderData();
  
  // Fetch customer data from Firebase
  const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
  
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const prevItemsRef = useRef<string>('');

  // Get form status for the status indicator
  const formStatus = getFormStatus('heritage');

  // Customer data sync is now handled by CustomerAndOrderSections component

  // Reset initialization when storeId changes
  useEffect(() => {
    setIsInitialized(false);
    setSelectedItems({});
    prevItemsRef.current = '';
  }, [storeId]);

  // Sync selected items with global order data (only on initial load, once per storeId)
  // Also reset when items become empty (after finalization)
  useEffect(() => {
    if (orderData.heritageItems === undefined) {
      prevItemsRef.current = '';
      return;
    }
    
    // Create a stable string representation for comparison
    const currentItemsKey = orderData.heritageItems.length > 0
      ? orderData.heritageItems
          .map(item => `${item.id}:${item.quantity}`)
          .sort()
          .join('|')
      : '';
    
    // Only process if items actually changed (prevents infinite loops)
    if (currentItemsKey === prevItemsRef.current) return;
    
    const itemsLength = orderData.heritageItems.length;
    
    if (itemsLength === 0 && isInitialized) {
      // Reset if items were cleared (e.g., after finalization)
      setSelectedItems({});
      setIsInitialized(false); // Allow re-initialization when new items are added
      prevItemsRef.current = currentItemsKey;
      console.log('🔄 Heritage form reset - items cleared');
    } else if (itemsLength > 0 && !isInitialized) {
      // Initialize with items from StoreOrders
      const itemsMap: { [key: string]: number } = {};
      orderData.heritageItems.forEach(item => {
        // Use exact quantity from StoreOrders - no transformation
        itemsMap[item.id] = item.quantity || 0;
      });
      setSelectedItems(itemsMap);
      setIsInitialized(true);
      prevItemsRef.current = currentItemsKey;
      console.log('✅ Heritage form initialized with', itemsLength, 'items');
    } else if (itemsLength === 0 && !isInitialized) {
      // Items are empty and not initialized - just mark as processed to prevent re-running
      prevItemsRef.current = currentItemsKey;
    }
    // Only depend on length and isInitialized to prevent infinite loops
    // The ref comparison inside prevents unnecessary state updates when array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.heritageItems?.length, isInitialized]);

  const handleInputChange = (field: keyof SharedOrderFormData, value: string | boolean) => {
    updateOrderData({ [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Trigger auto-save
    autoSave();
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    const newSelectedItems = {
      ...selectedItems,
      [productId]: Math.max(0, quantity)
    };
    setSelectedItems(newSelectedItems);
    
    // Update global order data
    const orderItems: OrderItem[] = Object.entries(newSelectedItems)
      .filter(([_, qty]) => qty > 0)
      .map(([id, qty]) => {
        const product = heritageProducts.find(p => p.id === id);
        return {
          id,
          packetDescription: product?.name || '',
          code: product?.code || '',
          quantity: qty,
          price: product?.price || 0,
          category: product?.category || 'organic'
        };
      });
    
    updateHeritageItems(orderItems);
    // Trigger auto-save
    autoSave();
  };


  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const calculateTotal = () => {
    return Object.entries(selectedItems).reduce((total, [productId, quantity]) => {
      const product = heritageProducts.find(p => p.id === productId);
      return total + (product ? product.price * quantity : 0);
    }, 0);
  };

  // Filter and group products
  const filteredProducts = useMemo(() => {
    return heritageProducts.filter(product => {
      const translatedName = getTranslatedProductName(product.name, t);
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           translatedName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.code.includes(searchTerm);
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const key = `${product.price.toFixed(2)} ${product.category}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(product);
      return acc;
    }, {} as { [key: string]: typeof heritageProducts });
  }, [filteredProducts]);

  const selectedItemsCount = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
  const totalAmount = calculateTotal();

  // Calculate global totals for all forms
  // NOTE: This is memoized for performance but should NOT trigger updates
  // The GlobalOrderSummary component will read from orderData directly
  const globalTotals = React.useMemo(() => {
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    
    const totalItems = allItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = allItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return { totalItems, totalAmount };
    // Use stable array length references instead of full orderData object
    // GlobalOrderSummary reads from orderData directly via useGlobalOrderData
  }, [
    orderData.heritageItems?.length,
    orderData.livingstonItems?.length,
    orderData.flowerItems?.length,
    orderData.vegetableItems?.length
  ]);

  return (
    <OptimizedFormContainer>
      {/* Form Status Indicator */}
      <FormStatusIndicator
        formType="heritage"
        status={formStatus}
        onReset={clearDraft}
      />
      
      {/* Product Selection */}
      <OptimizedFormSection>
          <SectionTitle 
            icon={Package} 
            title={t('forms.productSelection')} 
            subtitle={t('forms.heritage.subtitle')}
          />
          
          {/* Search and Filter */}
          <SearchAndFilterContainer>
            <SearchInput>
              <Search className="search-icon" size={20} />
              <input
                className="search-field"
                type="text"
                placeholder={t('forms.searchProducts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
            
            <FilterSelect>
              <select
                className="filter-field"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t('forms.allCategories')}</option>
                <option value="organic">{t('categories.organic')}</option>
                <option value="heritage">{t('categories.heritage')}</option>
                <option value="seedtape">{t('categories.seedtape')}</option>
              </select>
            </FilterSelect>
          </SearchAndFilterContainer>

          {/* Product Categories */}
          {Object.entries(groupedProducts).map(([categoryKey, products]) => {
            const sectionId = `category-${categoryKey}`;
            const isCollapsed = collapsedSections[sectionId];
            
            return (
              <ProductCategorySection key={categoryKey}>
                <CategoryHeader>
                  <div className="category-info">
                    <div className="category-title">
                      ${products[0].price.toFixed(2)} {products[0].category} Seeds
                    </div>
                    <div className="category-count">
                      {products.length} {products.length === 1 ? t('common.item') : t('common.items')}
                    </div>
                  </div>
                  <div className="category-price">
                    ${products[0].price.toFixed(2)} each
                  </div>
                </CategoryHeader>
                
                <OptimizedProductGrid>
                  {products.map((product) => {
                    const quantity = selectedItems[product.id] || 0;
                    const isSelected = quantity > 0;
                    
                    return (
                      <OptimizedProductCard 
                        key={product.id} 
                        isSelected={isSelected}
                      >
                        <div className="product-header">
                          <div className="product-title">{getTranslatedProductName(product.name, t)}</div>
                          <div className="product-price">${product.price.toFixed(2)}</div>
                        </div>
                        
                        <div className="product-code">Code: {product.code}</div>
                        
                        <CategoryBadge category={product.category} />
                        
                        <QuantityControl
                          value={quantity}
                          onChange={(newQuantity) => handleQuantityChange(product.id, newQuantity)}
                        />
                      </OptimizedProductCard>
                    );
                  })}
                </OptimizedProductGrid>
              </ProductCategorySection>
            );
          })}

          {errors.items && (
            <ErrorMessage>
              <AlertCircle size={20} />
              {errors.items}
            </ErrorMessage>
          )}
        </OptimizedFormSection>

        {/* Global Order Summary */}
        <GlobalOrderSummary />

              {/* Preview Order Button with Validation */}
              <PreviewOrderButton 
                onPreview={() => {
                  if (onShowPreview) {
                    onShowPreview();
                  } else {
                    console.log('Preview functionality not available');
                  }
                }}
              />
        
        {/* Back to Top Button */}
        <BackToTopButton scrollThreshold={300} />
    </OptimizedFormContainer>
  );
};
